import {
  Box,
  CircularProgress,
  Stack,
  StyledEngineProvider,
} from "@mui/material";
import * as Sentry from "@sentry/react";
import classNames from "classnames";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AvatarSize } from "../../../../../components/common/Avatar";
import { DialogModal } from "../../../../../components/common/dialog";
import Paginations from "../../../../../components/common/Pagination";
import ProfileCard from "../../../../../components/common/profileCard";
import { RatingIcon } from "../../../../../components/rating/ratingIcon";
import { EmptyFeedbackList } from "../../../../../components/userProfile/feedBackReviews/emptyFeedbackList/emptyFeedackList";
import ListFeedbackItem from "../../../../../components/userProfile/feedBackReviews/listFeedbackItem";
import { fetchFeedbackReview } from "../../../../../setup/api/services/feedbackAndReview";
import { getBasicUserDetailsById } from "../../../../../setup/api/services/user";
import {
  IUserInfoResponse,
  IUserInfoResponseData,
} from "../../../../../setup/api/services/user/models";
import { RatingCard } from "./ratingCard";
import styles from "./styles.module.scss";

interface Props {
  toggleModal: () => void;
  userId: string;
  modalOpen: boolean;
}

export const ShowFeedbackAndReview = (props: Props) => {
  const { userId, modalOpen, toggleModal } = props;
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [pagesCount, setPagesCount] = useState(1);
  const [userDetails, setUserDetails] = useState<IUserInfoResponseData>();

  const pageSize = 10;
  const [loading, setLoading] = useState<boolean>(false);
  const [feedbackReview, setFeedbackReview] = useState([]);

  const fetchListFeedbackReview = useCallback(
    async (page) => {
      try {
        setLoading(true);
        const response = await fetchFeedbackReview(userId, page - 1, pageSize);
        setFeedbackReview(response.data.content);
        setPagesCount(response.data.totalPages);
      } catch (error) {
        Sentry.captureException(error);
        console.error;
      }
      setLoading(false);
    },
    [userId]
  );

  useEffect(() => {
    fetchListFeedbackReview(page);
  }, [fetchListFeedbackReview, page]);

  const handlePaginationChange = (value: number) => {
    setPage(value);
    fetchListFeedbackReview(value);
  };

  const fetchUserBasicInfo = useCallback(async () => {
    if (userId) {
      try {
        const response: IUserInfoResponse = await getBasicUserDetailsById(
          userId
        );
        if (response) {
          setUserDetails(response.data);
        }
      } catch (error) {
        Sentry.captureException(error);
        console.error(error);
      }
    }
  }, [userId]);

  useEffect(() => {
    fetchUserBasicInfo();
  }, []);

  const renderModal = () => {
    return (
      <DialogModal
        className={styles["review_modal"]}
        title={t("lbl_profile_list_reviews")}
        open={modalOpen}
        handleClose={toggleModal}>
        {loading ? (
          <Box className={classNames(styles["container"], "center")}>
            <CircularProgress />
          </Box>
        ) : feedbackReview.length !== 0 ? (
          <>
            <div className={classNames(styles["top_section"])}>
              <div>
                <RatingCard
                  feedbackOverallRatingScore={
                    userDetails?.profile?.feedbackOverallRatingScore
                  }
                  feedbackReviewCount={feedbackReview.length}
                />
                <RatingIcon
                  readonly
                  initialValue={
                    userDetails?.profile?.feedbackOverallRatingScore
                  }
                />
              </div>
              <ProfileCard
                name={
                  userDetails?.profile?.fullName
                    ? `${userDetails?.profile?.fullName}`
                    : ""
                }
                welcomeText={""}
                phoneNumber={userDetails?.phoneNumber || ""}
                profile={userDetails?.profile?.photoUrl || ""}
                avatarSize={AvatarSize.MEDIUM}
                nationalIdVerified={userDetails?.nationalIdVerified}
              />
            </div>
            <div className={styles.feedback_list_container} dir="rtl">
              {feedbackReview.map((item: any) => {
                return (
                  <ListFeedbackItem key={item.id} item={item} isShowModal />
                );
              })}
            </div>
            <Paginations
              length={feedbackReview.length}
              page={page}
              pagesCount={pagesCount}
              handlePaginationChange={handlePaginationChange}
            />
          </>
        ) : (
          <EmptyFeedbackList />
        )}
      </DialogModal>
    );
  };

  return (
    <StyledEngineProvider injectFirst>
      <Stack spacing={2} justifyContent="center" width={"100%"}>
        {renderModal()}
      </Stack>
    </StyledEngineProvider>
  );
};
